import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import type {
    DiaryCarryOverOffer,
    DiaryCarryOverState
} from './diaryCarryOver';

/**
 * What each state says and shows. All three are the same disabled-or-not icon
 * button, so a row never changes shape — and every one of them can be hovered
 * for the reason, which an empty cell could not.
 */
const STATE_ICONS: Record<
    DiaryCarryOverState,
    { titleId: string; Icon: typeof CheckIcon }
> = {
    open: { titleId: 'diary.numbers.carryOverApply', Icon: ArrowUpwardIcon },
    taken: { titleId: 'diary.numbers.carryOverTaken', Icon: CheckIcon },
    blocked: { titleId: 'diary.numbers.carryOverBlocked', Icon: EditIcon }
};

/**
 * What the previous Tagebuch day said, offered row by row — the "Letzte
 * vergebene Nrn." AND its Sondagen with their Teilfotos in ONE list.
 *
 * Built like the day's crew list (DiaryDayPeopleList): each row carries its
 * value into the form, the footer takes everything still missing at once, and a
 * row already carried over says so with a tick instead of a button. That last
 * part is why this is a list and not a placeholder in each field: a placeholder
 * disappears exactly when something has been typed, so it can never answer "did
 * I already deal with this one?".
 *
 * Nothing writes itself, and nothing OVERWRITES: a row whose field has been
 * filled with a different number keeps its place in the list but loses its
 * button (see DiaryCarryOverState) — the day records what the crew wrote down.
 */
export default function DiaryPreviousNumbersList({
    date,
    offers,
    missingCount,
    onTake,
    onTakeAll
}: {
    /** The day these come from, already formatted. */
    date: string;
    offers: DiaryCarryOverOffer[];
    /** How many of them the day being written does not say yet. */
    missingCount: number;
    onTake: (offer: DiaryCarryOverOffer) => void;
    onTakeAll: () => void;
}) {
    const intl = useIntl();

    /** A row's two columns: what it is, and what the previous day made of it. */
    function columns(offer: DiaryCarryOverOffer): [string, string] {
        if (offer.kind === 'number') {
            return [
                intl.formatMessage({ id: `diary.field.${offer.field}` }),
                offer.value
            ];
        }
        // A Sondage row names both halves, each with its own label — either may
        // be blank (a Sondage not photographed, a Teilfoto of something else).
        const label = (id: string, value: string) =>
            value ? `${intl.formatMessage({ id })} ${value}` : '';
        return [
            label('diary.field.lastSondageNumber', offer.entry.sondage),
            label(
                'diary.field.lastPartialPhotoNumber',
                offer.entry.partialPhoto
            )
        ];
    }

    return (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 0.75 }}>
                <Typography variant="subtitle2">
                    <FormattedMessage
                        id="diary.numbers.carryOverTitle"
                        values={{ date }}
                    />
                </Typography>
            </Box>
            <Divider />
            <Stack divider={<Divider />}>
                {offers.map((offer) => {
                    const [name, value] = columns(offer);
                    const { titleId, Icon } = STATE_ICONS[offer.state];
                    const title = intl.formatMessage({ id: titleId });
                    return (
                        <Box
                            key={offer.key}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                /**
                                 * Dense, like a DataGrid in its compact
                                 * density: the row is one short line, and a day
                                 * can offer half a dozen of them. The button
                                 * sets the height, so the padding only has to
                                 * stay out of its way.
                                 */
                                py: 0.25,
                                minHeight: 36
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ flex: 1, minWidth: 0 }}
                                noWrap
                            >
                                {name}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                                noWrap
                            >
                                {value}
                            </Typography>
                            <Tooltip title={title}>
                                {/* A disabled button fires no events, so the
                                    tooltip needs a wrapper. */}
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={offer.state !== 'open'}
                                        aria-label={title}
                                        onClick={() => onTake(offer)}
                                    >
                                        <Icon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                })}
                {/* A day that has taken everything (or may take nothing) is
                    left with its rows: "Alle 0 uebernehmen", greyed out, is a
                    button about nothing. The list itself stays — what the
                    previous day handed out is worth reading either way. */}
                {missingCount > 0 && (
                    <Box sx={{ px: 1, py: 0.25 }}>
                        <Button
                            size="small"
                            startIcon={<ArrowUpwardIcon />}
                            onClick={onTakeAll}
                        >
                            <FormattedMessage
                                id="diary.numbers.carryOverAll"
                                values={{ count: missingCount }}
                            />
                        </Button>
                    </Box>
                )}
            </Stack>
        </Paper>
    );
}
